import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl, apiErr } from "@/lib/api";
import { Field, Input, Select, Button, AddableSelect } from "@/lib/ui";

const UNITS = ["Box", "Piece", "Sq.ft", "Kg", "Set", "Pair", "Meter", "Bundle"];

function Uploader({ label, value, accept, onChange, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const isVideo = accept.includes("video");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(`${label} uploaded`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">{label} <span className="text-orange-600">*</span></p>
      <div onClick={() => ref.current.click()} data-testid={testid}
        className="flex aspect-video cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:border-orange-400">
        {busy ? <span className="text-sm text-slate-400"><i className="fa-solid fa-spinner fa-spin" /> Uploading…</span> :
          value ? (isVideo ? <video src={mediaUrl(value)} className="h-full w-full object-cover" /> : <img src={mediaUrl(value)} className="h-full w-full object-cover" alt={label} />) :
            <span className="text-center text-xs text-slate-400"><i className={`fa-solid ${isVideo ? "fa-video" : "fa-camera"} mb-1 block text-xl`} />Click to upload</span>}
      </div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
}

const EMPTY = {
  category: "", product: "", size: "", quality: "", condition: "New", quantity: "", moq: "",
  primary_unit: "Box", secondary_unit: "", actual_rate: "", primary_rate: "", secondary_rate: "",
  catalogue_image: "", stock_image: "", stock_video: "", city: "", locality: "",
  manufacturer_stock: false, pan_india: false, allow_buyers: false, description: "", keywords: "",
};

export default function AddProduct() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({ categories: [], sizes: [], cities: [], localities: [] });
  const [restricted, setRestricted] = useState(null);
  const [banner, setBanner] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/filters").then(({ data }) => setFilters(data));
    api.get("/public/disclaimers", { params: { location: "Add Product" } }).then(({ data }) => data[0] && setBanner(data[0].text));
    if (!editing) api.get("/seller/dashboard").then(({ data }) => {
      const s = data.slots;
      if (!s.subscribed) { toast.info("Choose a package to start listing your stock"); nav("/seller/app/subscription", { replace: true }); }
      else if (s.used >= s.total) { toast.info(`All ${s.total} product slots are used — add on a package to list more`); nav("/seller/app/subscription", { replace: true }); }
    });
    if (editing) api.get("/seller/products").then(({ data }) => {
      const p = data.find((x) => x.id === id);
      if (p) setForm({ ...EMPTY, ...p, quantity: p.quantity, moq: p.moq });
    });
  }, [id, editing, nav]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v, ...(k === "city" ? { locality: "" } : {}) }));

  const addMaster = async (kind, name, city) => {
    await api.post(`/seller/master/${kind}`, { name, city });
    const { data } = await api.get("/public/filters");
    setFilters(data);
  };

  // Real-time restricted check on category/product
  const checkRestricted = useCallback(async (text, deep) => {
    if (!text.trim()) { setRestricted(null); return false; }
    try {
      const { data } = await api.post("/products/check-restricted", { text, deep });
      if (data.restricted) { setRestricted(data); return true; }
      setRestricted(null); return false;
    } catch { return false; }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => checkRestricted(`${form.category} ${form.product}`, false), 400);
    return () => clearTimeout(t);
  }, [form.category, form.product, checkRestricted]);

  const discount = form.actual_rate && form.primary_rate && Number(form.actual_rate) > 0
    ? Math.max(0, Math.round(((Number(form.actual_rate) - Number(form.primary_rate)) / Number(form.actual_rate)) * 100)) : 0;

  const submit = async (e) => {
    e.preventDefault();
    // deep AI check on product name field before submit
    const blocked = await checkRestricted(form.product, true);
    if (blocked) {
      set("product", "");
      toast.error("RESTRICTED PRODUCT — this item is not allowed. Field cleared.");
      return;
    }
    if (!form.catalogue_image || !form.stock_image) { toast.error("2 images are required (catalogue + original stock)"); return; }
    if (!form.category || !form.product || !form.size || !form.city || !form.locality) { toast.error("Please fill all required fields"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form, quantity: Number(form.quantity) || 0, moq: Number(form.moq) || 0,
        actual_rate: Number(form.actual_rate), primary_rate: Number(form.primary_rate),
        secondary_rate: Number(form.secondary_rate) || 0,
      };
      if (editing) await api.put(`/seller/products/${id}`, payload);
      else await api.post("/seller/products", payload);
      toast.success(editing ? "Updated — resubmitted for approval" : "Submitted! Approval target within 48 hours.");
      nav("/seller/app/products");
    } catch (err) {
      const msg = apiErr(err);
      if (msg.includes("RESTRICTED")) { set("category", ""); set("product", ""); }
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const locs = filters.localities.filter((l) => !form.city || l.city === form.city);

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">{editing ? "Edit Product" : "Add Product"}</h1>
      <p className="mt-1 text-sm text-slate-500">2 images + 1 real stock video. Discount is auto-calculated.</p>

      {banner && (
        <div data-testid="food-banner" className="mt-4 rounded-lg border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          <i className="fa-solid fa-ban mr-1" /> {banner}
        </div>
      )}

      {restricted && (
        <div data-testid="restricted-item-warning-alert" className="mt-4 rounded-lg border border-red-300 bg-red-100 px-4 py-3">
          <p className="font-head font-extrabold text-red-700">RESTRICTED PRODUCT</p>
          <p className="text-sm text-red-600">"{restricted.matched}" is not allowed to be listed on Deadstock.live. Please enter a permitted value.</p>
        </div>
      )}

      <form onSubmit={submit} className="mt-5 space-y-6">
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2">
          <Field label="Category" required>
            <AddableSelect testid="ap-category" addTestid="ap-category-add" value={form.category} placeholder="Select"
              options={filters.categories.map((c) => c.name)} onChange={(v) => set("category", v)}
              onAdd={(name) => addMaster("categories", name)} />
          </Field>
          <Field label="Product Name" required>
            <Input data-testid="ap-product" value={form.product} onChange={(e) => set("product", e.target.value)} placeholder="e.g. Premium Vitrified Tile" />
          </Field>
          <Field label="Size" required>
            <AddableSelect testid="ap-size" addTestid="ap-size-add" value={form.size} placeholder="Select"
              options={filters.sizes.map((s) => s.name)} onChange={(v) => set("size", v)}
              onAdd={(name) => addMaster("sizes", name)} />
          </Field>
          <Field label="Quality / Grade"><Input data-testid="ap-quality" value={form.quality} onChange={(e) => set("quality", e.target.value)} placeholder="Premium / A Grade" /></Field>
          <div className="sm:col-span-2">
            <Field label="Product Description" hint="Describe the stock — condition details, packaging, brand, why it's a great deal, etc.">
              <textarea data-testid="ap-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" placeholder="Add a detailed description of the product…" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Search Keywords" hint="Comma-separated words buyers might search (e.g. sofa, couch, wooden). The product shows up when any keyword is searched.">
              <Input data-testid="ap-keywords" value={form.keywords} onChange={(e) => set("keywords", e.target.value)} placeholder="sofa, couch, wooden furniture" />
            </Field>
          </div>
          <Field label="Condition" required>
            <Select data-testid="ap-condition" value={form.condition} onChange={(e) => set("condition", e.target.value)}>
              <option>New</option><option>Used</option><option>Refurbished</option>
            </Select>
          </Field>
          <Field label="Available Quantity" required><Input data-testid="ap-qty" type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></Field>
          <Field label="MOQ" hint="Leave blank / 0 for No MOQ"><Input data-testid="ap-moq" type="number" value={form.moq} onChange={(e) => set("moq", e.target.value)} /></Field>
          <div />
          <Field label="Primary Unit" required>
            <Select data-testid="ap-punit" value={form.primary_unit} onChange={(e) => set("primary_unit", e.target.value)}>
              {UNITS.map((u) => <option key={u}>{u}</option>)}
            </Select>
          </Field>
          <Field label="Secondary Unit (optional)">
            <Select data-testid="ap-sunit" value={form.secondary_unit} onChange={(e) => set("secondary_unit", e.target.value)}>
              <option value="">None</option>
              {UNITS.map((u) => <option key={u}>{u}</option>)}
            </Select>
          </Field>
        </div>

        {/* Pricing */}
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-3">
          <Field label="Actual Rate (₹)" required hint="Original / regular rate"><Input data-testid="ap-actual" type="number" value={form.actual_rate} onChange={(e) => set("actual_rate", e.target.value)} /></Field>
          <Field label="Primary Unit Selling Rate (₹)" required><Input data-testid="ap-prate" type="number" value={form.primary_rate} onChange={(e) => set("primary_rate", e.target.value)} /></Field>
          <Field label="Secondary Unit Selling Rate (₹)"><Input data-testid="ap-srate" type="number" value={form.secondary_rate} onChange={(e) => set("secondary_rate", e.target.value)} disabled={!form.secondary_unit} /></Field>
          <div className="sm:col-span-3">
            <div className="inline-flex items-center gap-2 rounded-lg bg-orange-50 px-4 py-2 text-sm font-bold text-orange-700" data-testid="ap-discount">
              Auto Discount: {discount}% OFF
            </div>
          </div>
        </div>

        {/* Media */}
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-3">
          <Uploader testid="ap-catalogue" label="Catalogue Image" accept="image/*" value={form.catalogue_image} onChange={(v) => set("catalogue_image", v)} />
          <Uploader testid="ap-stock" label="Original Stock Image" accept="image/*" value={form.stock_image} onChange={(v) => set("stock_image", v)} />
          <Uploader testid="ap-video" label="Real Stock Video" accept="video/*" value={form.stock_video} onChange={(v) => set("stock_video", v)} />
        </div>

        {/* Location & flags */}
        <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:grid-cols-2">
          <Field label="City" required>
            <AddableSelect testid="ap-city" addTestid="ap-city-add" value={form.city} placeholder="Select"
              options={filters.cities.map((c) => c.name)} onChange={(v) => set("city", v)}
              onAdd={(name) => addMaster("cities", name)} />
          </Field>
          <Field label="Locality" required>
            <AddableSelect testid="ap-locality" addTestid="ap-locality-add" value={form.locality} placeholder="Select"
              disabled={!form.city} options={locs.map((l) => l.name)} onChange={(v) => set("locality", v)}
              onAdd={(name) => addMaster("localities", name, form.city)} />
          </Field>
          <label className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <div><p className="font-semibold text-slate-800">Manufacturer Stock</p><p className="text-xs text-slate-500">Visible only to retailers & wholesalers (B2B)</p></div>
            <input data-testid="ap-mfg" type="checkbox" checked={form.manufacturer_stock} onChange={(e) => set("manufacturer_stock", e.target.checked)} className="h-5 w-5" />
          </label>
          {form.manufacturer_stock && (
            <label className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50/50 px-4 py-3 sm:col-span-2">
              <div><p className="font-semibold text-slate-800">Allow End-User (Buyer) Visibility</p><p className="text-xs text-slate-500">Also show this manufacturer stock to buyers in the public marketplace</p></div>
              <input data-testid="ap-allowbuyers" type="checkbox" checked={form.allow_buyers} onChange={(e) => set("allow_buyers", e.target.checked)} className="h-5 w-5" />
            </label>
          )}
          <label className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <div><p className="font-semibold text-slate-800">Sell Pan India</p><p className="text-xs text-slate-500">Discoverable throughout India</p></div>
            <input data-testid="ap-pan" type="checkbox" checked={form.pan_india} onChange={(e) => set("pan_india", e.target.checked)} className="h-5 w-5" />
          </label>
        </div>

        <Button data-testid="add-product-submit-btn" type="submit" disabled={loading || !!restricted} className="w-full py-3 text-base">
          {loading ? "Submitting…" : editing ? "Update & Resubmit" : "Submit for Approval"}
        </Button>
      </form>
    </div>
  );
}
